/**
 * 订单表单弹窗数据加工纯函数：初始表单值、服务时长计算、明细行工厂与表单样式工具
 * （付款状态判定不在这里，web 端唯一实现在 `utils/paymentAmount.ts`）
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { OrderItem, OrderItemDetail } from '../../../../types';
import { bookingDays } from '../../../../utils/dateUtils';
import type { OrderFormData } from '../OrderFormSchema';

// 配置 Day.js 插件
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Shanghai');

export function calcDuration(startDate: string, endDate: string, isFullDay: boolean, timeStart: string, timeEnd: string): string {
  if (!startDate) return '';
  if (isFullDay) {
    // 天数未知（档期还没选）就不显示提示，不能拼成「null天」
    const days = bookingDays(startDate, endDate);
    return days === null ? '' : `${days}天`;
  }
  if (!timeStart || !timeEnd) return '';
  const [sh, sm] = timeStart.split(':').map(Number);
  const [eh, em] = timeEnd.split(':').map(Number);
  let diffMins = (eh * 60 + em) - (sh * 60 + sm);
  if (endDate && endDate !== startDate) {
    const s = dayjs(startDate);
    const e = dayjs(endDate);
    diffMins += e.diff(s, 'day') * 1440;
  }
  if (diffMins <= 0) return '';
  const h = Math.floor(diffMins / 60), m = diffMins % 60;
  if (h === 0) return `${m}分钟`;
  if (m === 0) return `${h}小时`;
  return `${h}小时${m}分钟`;
}

export function emptyItem(): OrderItemDetail {
  return { itemName: '', itemSpec: null, quantity: 1, unitPrice: 0 };
}

// ── 时间安排跨字段规则 ──
// 这四条是「填完不能破坏档期」的闸门，算法与文案与后端 validateArrivalDateTime、
// hmos OrderFlowCommon、Flutter order_form_rules 逐条同源；只允许这一份，
// 弹窗与 schema 各自 import，避免 change 时机校验了、提交时又被 resolver 重算冲掉。

/** 预计到达时间的最晚可填值：自定义时段=开始时间前 30 分钟；全天=当天 20:00；只填日期=23:59 兜底 */
export function arrivalMaxOf(orderStartDate: string, isFullDay: boolean, orderTimeStart: string): string {
  if (!orderStartDate) return '';
  if (!isFullDay && orderTimeStart) {
    const [h, m] = orderTimeStart.split(':').map(Number);
    let totalMins = h * 60 + m - 30;
    if (totalMins < 0) totalMins = 0;
    const maxH = Math.floor(totalMins / 60);
    const maxM = totalMins % 60;
    return `${orderStartDate}T${String(maxH).padStart(2, '0')}:${String(maxM).padStart(2, '0')}`;
  }
  if (isFullDay) return `${orderStartDate}T20:00`;
  return `${orderStartDate}T23:59`;
}

/** 到达时间校验，合法返回 undefined。入参均为表单原值，'YYYY-MM-DDTHH:mm' 直接按字符串比 */
export function arrivalErrorOf(
  val: string,
  orderStartDate: string,
  isFullDay: boolean,
  orderTimeStart: string
): string | undefined {
  if (!val || !orderStartDate) return undefined;
  if (val.split('T')[0] > orderStartDate) {
    return '预计到达时间不得晚于预约日期当天';
  }
  const max = arrivalMaxOf(orderStartDate, isFullDay, orderTimeStart);
  if (!max || val <= max) return undefined;
  if (!isFullDay && orderTimeStart) {
    return `自定义时间段时，预计到达时间不得晚于开始时间前30分钟（最晚 ${max.split('T')[1]}）`;
  }
  if (isFullDay) return '全天服务时，预计到达时间不得晚于当天 20:00';
  return undefined;
}

/** 结束日期：不得早于预约日期 */
export function endDateErrorOf(orderStartDate: string, orderEndDate: string): string | undefined {
  if (orderEndDate && orderStartDate && orderEndDate < orderStartDate) {
    return '结束日期不能早于开始日期';
  }
  return undefined;
}

/** 结束时间：同一天时不得早于（含等于）开始时间；跨天或全天不看这条 */
export function endTimeErrorOf(
  orderStartDate: string,
  orderEndDate: string,
  isFullDay: boolean,
  orderTimeStart: string,
  orderTimeEnd: string
): string | undefined {
  if (isFullDay || !orderTimeEnd) return undefined;
  const sameDay = !orderEndDate || orderEndDate === orderStartDate;
  if (sameDay && orderTimeStart && orderTimeEnd <= orderTimeStart) {
    return '结束时间不能早于开始时间';
  }
  return undefined;
}

// ── 金额与明细行闸门 ──
// 文案与后端 `order-item.dto.ts` / Flutter `order_form_rules.dart` / hmos `OrderFlowCommon.ets` 逐字同源。
// 此前 web 一行都不挡：明细行没名称就静默丢掉（用户以为存了 3 条实际 2 条），
// `type=number` 也只提示不拦，1.999 能一路发到后端、被 Decimal(10,2) 四舍五入成 2.00，账对不上查不出原因。

/** Decimal(10,2) 列能装下的最大值（后端 order-limits 同一数值） */
export const ORDER_AMOUNT_MAX = 99999999.99;
/** quantity 列的合理上限（与后端 @Max(999999) 一致） */
export const ITEM_QTY_MAX = 999999;
/** 单笔订单明细条数上限（后端 @ArrayMaxSize(ORDER_ITEMS_MAX)）：两端移动表单早就在「添加」处挡下，web 不能漏 */
export const ORDER_ITEMS_MAX = 100;

// 金额夹两位小数的 `roundMoney` 在 `../../../../utils/paymentAmount.ts`：
// 那是金额口径的单一事实源，订单表单与核算页的抵消金额求和共用同一条，不在这里再留一份。

/**
 * 金额闸门：允许留空（订单金额/已付金额空着就是 0，0 元单是真实场景），
 * 但填了就必须最多两位小数且不超列宽。
 */
export function amountError(label: string, raw: string | number | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return `${label}最多两位小数`;
  if (Number(s) > ORDER_AMOUNT_MAX) return `${label}不能超过 ${ORDER_AMOUNT_MAX}`;
  return '';
}

/** 明细数量闸门：空=没填（0 是合法值，「没填」与「填了 0」不能混） */
export function quantityError(raw: string | number | null | undefined): string {
  const s = String(raw ?? '').trim();
  if (s === '') return '请填写数量';
  if (!/^\d+(\.\d+)?$/.test(s)) return '数量必须是数字';
  if (Number(s) > ITEM_QTY_MAX) return `数量超出合理范围（最大 ${ITEM_QTY_MAX}）`;
  return '';
}

/** 一行的「用户根本没动过」判定：新增行默认就是 名称空/规格空/数量 1/单价 0 */
function isBlankRow(row: OrderItemDetail): boolean {
  const qty = String(row.quantity ?? '').trim();
  const price = String(row.unitPrice ?? '').trim();
  return !row.itemName?.trim()
    && !row.itemSpec?.trim()
    && (qty === '' || qty === '1')
    && (price === '' || price === '0');
}

/** 明细逐行闸门，返回第一条错误的完整句子；web 与两端同为单条汇总提示，故都带「第 N 行：」 */
export function itemRowsError(rows: OrderItemDetail[]): string {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (isBlankRow(row)) continue;
    if (!row.itemName?.trim()) return `第 ${i + 1} 行：请填写明细项目名称`;
    const qtyErr = quantityError(row.quantity);
    if (qtyErr) return `第 ${i + 1} 行：${qtyErr}`;
    const priceErr = amountError('单价', row.unitPrice);
    if (priceErr) return `第 ${i + 1} 行：${priceErr}`;
  }
  return '';
}

export function initForm(order?: OrderItem | null): OrderFormData {
  if (!order) return {
    orderCategory: '', serviceTypeId: '', customerId: '',
    contactPerson: '', contactPhone: '', activityName: '', activityAddress: '',
    orderStartDate: '', orderEndDate: '', isFullDay: true,
    orderTimeStart: '', orderTimeEnd: '', arrivalDateTime: '',
    orderAmount: '', paidAmount: '',
    paymentMethod: '', paymentDate: '', orderSpecialInstructions: '',
  };
  return {
    orderCategory: order.orderCategory ?? '',
    serviceTypeId: order.serviceType?.name ?? '',
    customerId: order.customer?.id ?? '',
    contactPerson: order.contactPerson ?? '',
    contactPhone: order.contactPhone ?? '',
    activityName: order.activityName ?? '',
    activityAddress: order.activityAddress ?? '',
    orderStartDate: order.orderStartDate ?? '',
    orderEndDate: order.orderEndDate ?? '',
    isFullDay: order.isFullDay ?? true,
    orderTimeStart: order.orderTimeStart ?? '',
    orderTimeEnd: order.orderTimeEnd ?? '',
    arrivalDateTime: order.arrivalDateTime ? order.arrivalDateTime.slice(0, 16) : '',
    orderAmount: order.orderAmount != null ? String(order.orderAmount) : '',
    paidAmount: order.paidAmount != null ? String(order.paidAmount) : '',
    paymentMethod: order.paymentMethod ?? '',
    paymentDate: order.paymentDate ?? '',
    orderSpecialInstructions: order.orderSpecialInstructions ?? '',
  };
}

export const inp = (err?: string) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-all bg-white ${err ? 'border-rose-300 focus:ring-rose-200' : 'border-gray-200 focus:ring-teal-500/20 focus:border-teal-400'}`;
export const lbl = 'block text-xs font-medium text-gray-500 mb-1.5';